import { createPlaceholderToken } from '@elfsight/lib-translator';
import type { LanguageCode } from '../../../types';
import type { CustomTranslationsMap } from './parse-google-sheets-data';
import { normalizePhrase, preserveCapitalization } from './utils';

const CUSTOM_TRANSLATION_TOKEN_PREFIX = 'ELXCT';
const CUSTOM_TRANSLATION_TOKEN_SUFFIX = 'X';

function hasCJK(str: string) {
  return /[\p{Script=Han}]/u.test(str);
}

function escapeRegExp(string: string) {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function buildUnicodeWholeWordRegExp(phraseKey: string) {
  const escaped = escapeRegExp(phraseKey);

  if (hasCJK(phraseKey)) {
    return new RegExp(`(${escaped})`, 'giu');
  }

  return new RegExp(
    `(^|[^\\p{L}\\p{N}\\p{M}_])(${escaped})(?=[^\\p{L}\\p{N}\\p{M}_]|$)`,
    'giu'
  );
}

export type CustomTranslationPlan = {
  exactTranslation: string | null;
  phraseForTranslation: string;
  restoreTranslation: (translatedPhrase: string) => string;
};

type ApplyCustomTranslationsParams = {
  phrases: string[];
  targetLanguage: LanguageCode;
  customTranslations?: CustomTranslationsMap;
  excludedPhrases?: string[];
};

export function applyCustomTranslations({
  phrases,
  targetLanguage,
  customTranslations,
  excludedPhrases = []
}: ApplyCustomTranslationsParams) {
  if (!customTranslations || customTranslations.size === 0) {
    return phrases.map(createDefaultPlan);
  }

  const normalizedExcludedPhrases = new Set(
    excludedPhrases.map(normalizePhrase).filter(Boolean)
  );

  return phrases.map((phrase) => {
    // Шаг 1: Сначала пытаемся найти полное совпадение фразы (наивысший приоритет)
    const normalizedPhrase = normalizePhrase(phrase);
    if (normalizedExcludedPhrases.has(normalizedPhrase)) {
      return createDefaultPlan(phrase);
    }

    const phraseTranslations = customTranslations.get(normalizedPhrase);

    if (phraseTranslations) {
      const customTranslation = phraseTranslations.get(targetLanguage);
      if (customTranslation) {
        return {
          exactTranslation: customTranslation,
          phraseForTranslation: phrase,
          restoreTranslation: (translatedPhrase: string) => translatedPhrase
        };
      }
    }

    // Шаг 2: Для частичных совпадений подставляем плейсхолдеры,
    // чтобы отправить остальную часть фразы в переводчик.
    return createPartialTranslationPlan({
      phrase,
      targetLanguage,
      customTranslations,
      normalizedExcludedPhrases
    });
  });
}

type CreatePartialTranslationPlanParams = {
  phrase: string;
  targetLanguage: LanguageCode;
  customTranslations: CustomTranslationsMap;
  normalizedExcludedPhrases: Set<string>;
};

function createPartialTranslationPlan({
  phrase,
  targetLanguage,
  customTranslations,
  normalizedExcludedPhrases
}: CreatePartialTranslationPlanParams): CustomTranslationPlan {
  // Извлекаем все доступные фразовые шаблоны (отсортированные по длине, длинные первые)
  const availablePhrases = Array.from(customTranslations.keys())
    .filter(
      (key) =>
        customTranslations.get(key)?.has(targetLanguage) &&
        !normalizedExcludedPhrases.has(key)
    )
    .sort((a, b) => b.length - a.length); // Длинные первые для жадного поиска

  let phraseForTranslation = phrase;
  const placeholderByReplacementKey = new Map<string, string>();
  const translationByPlaceholder = new Map<string, string>();

  // Сначала пытаемся заменить многословные фразы (жадный подход)
  for (const phraseKey of availablePhrases) {
    const translation = customTranslations.get(phraseKey)?.get(targetLanguage);
    if (!translation) {
      continue;
    }

    const regex = buildUnicodeWholeWordRegExp(phraseKey);

    // Подменяем совпадения плейсхолдерами и запоминаем итоговый перевод.
    phraseForTranslation = phraseForTranslation.replace(
      regex,
      (_full, firstCapture: string, secondCaptureOrOffset: string | number) => {
        const hasPrefixCapture = typeof secondCaptureOrOffset === 'string';
        const prefix = hasPrefixCapture ? firstCapture : '';
        const match = hasPrefixCapture
          ? (secondCaptureOrOffset as string)
          : firstCapture;
        const replacementKey = `${phraseKey}:${match}`;
        const placeholder =
          placeholderByReplacementKey.get(replacementKey) ??
          createCustomTranslationPlaceholder(replacementKey);
        placeholderByReplacementKey.set(replacementKey, placeholder);
        translationByPlaceholder.set(
          placeholder,
          preserveCapitalization(match, translation)
        );

        return prefix + placeholder;
      }
    );
  }

  const placeholderEntries = Array.from(translationByPlaceholder.entries());

  if (placeholderEntries.length === 0) {
    return createDefaultPlan(phrase);
  }

  return {
    exactTranslation: null,
    phraseForTranslation,
    restoreTranslation: (translatedPhrase: string) =>
      placeholderEntries.reduce(
        (result, [placeholder, translation]) =>
          result.replace(
            new RegExp(escapeRegExp(placeholder), 'gi'),
            translation
          ),
        translatedPhrase
      )
  };
}

function createDefaultPlan(phrase: string): CustomTranslationPlan {
  return {
    exactTranslation: null,
    phraseForTranslation: phrase,
    restoreTranslation: (translatedPhrase: string) => translatedPhrase
  };
}

function createCustomTranslationPlaceholder(value: string) {
  return createPlaceholderToken({
    prefix: CUSTOM_TRANSLATION_TOKEN_PREFIX,
    value,
    suffix: CUSTOM_TRANSLATION_TOKEN_SUFFIX
  });
}
